import { useCallback, useRef } from 'react';
import { ClinicCanvasStage } from '../components/ClinicCanvasStage';
import { careChapters } from '../content';
import { useCareJourney } from '../hooks/useCareJourney';

export function CareJourneySection() {
  const canvasProgressRef = useRef(0);

  const handleProgress = useCallback((progress: number) => {
    canvasProgressRef.current = progress;
  }, []);

  const { sectionRef, pinRef, copyRef, activeChapter, prefersReducedMotion } = useCareJourney({
    chapterCount: careChapters.length,
    onProgress: handleProgress,
  });

  return (
    <section id="care" ref={sectionRef} className="relative bg-bone" aria-labelledby="care-heading">
      <div
        ref={pinRef}
        className="relative mx-auto grid min-h-[100svh] max-w-[var(--container-max)] grid-cols-1 items-center gap-12 px-[var(--section-padding-x)] py-[var(--section-padding-y)] lg:grid-cols-[0.95fr_1.05fr] lg:gap-20"
      >
        <div className="stage-frame aspect-[4/5] w-full max-w-xl justify-self-center lg:max-w-none">
          <div className="stage-frame-inner" aria-hidden="true" />
          <ClinicCanvasStage
            progressRef={prefersReducedMotion ? undefined : canvasProgressRef}
            pointerEnabled={!prefersReducedMotion}
          />
        </div>

        <div ref={copyRef} className="relative">
          <p className="section-eyebrow">Care journey</p>
          <h2 id="care-heading" className="section-title">
            Listen, treat, send you home clear
          </h2>

          {prefersReducedMotion ? (
            <div className="mt-10 space-y-10">
              {careChapters.map((chapter, index) => (
                <article key={chapter.id} data-chapter={chapter.id}>
                  <span className="font-body text-xs uppercase tracking-[0.3em] text-primary-600">
                    Chapter {index + 1}
                  </span>
                  <h3 className="mt-3 font-display text-2xl font-semibold text-ink sm:text-3xl">{chapter.title}</h3>
                  <p className="section-copy max-w-lg">{chapter.body}</p>
                </article>
              ))}
            </div>
          ) : (
            <>
              <div className="relative mt-10 min-h-[16rem]">
                {careChapters.map((chapter, index) => {
                  const isActive = index === activeChapter;

                  return (
                    <article
                      key={chapter.id}
                      data-chapter={chapter.id}
                      className="absolute inset-0 transition-all duration-500"
                      style={{
                        opacity: isActive ? 1 : 0,
                        transform: `translateY(${isActive ? 0 : 18}px)`,
                        pointerEvents: isActive ? 'auto' : 'none',
                      }}
                      aria-hidden={!isActive}
                    >
                      <span className="font-body text-xs uppercase tracking-[0.3em] text-primary-600">
                        Chapter {index + 1}
                      </span>
                      <h3 className="mt-3 font-display text-2xl font-semibold text-ink sm:text-3xl">{chapter.title}</h3>
                      <p className="section-copy max-w-lg">{chapter.body}</p>
                    </article>
                  );
                })}
              </div>

              <div className="mt-10 flex gap-2" aria-hidden="true">
                {careChapters.map((chapter, index) => (
                  <span
                    key={chapter.id}
                    className={`h-0.5 flex-1 transition-colors duration-300 ${
                      index <= activeChapter ? 'bg-primary-500' : 'bg-neutral-200'
                    }`}
                  />
                ))}
              </div>

              <p className="mt-6 font-body text-xs uppercase tracking-[0.28em] text-neutral-500">
                Chapter {activeChapter + 1} of {careChapters.length}
              </p>
            </>
          )}

          <p className="sr-only" aria-live="polite">
            Chapter {activeChapter + 1} of {careChapters.length}: {careChapters[activeChapter]?.title}
          </p>
        </div>
      </div>
    </section>
  );
}
